import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Eye, 
  Edit, 
  Download, 
  Share, 
  Trash2,
  FileText,
  Database,
  Image,
  Pill,
  UserCheck,
  Check
} from 'lucide-react';

export type PermissionType = 'read' | 'write' | 'download' | 'share' | 'delete';
export type DataType = 'medical_records' | 'documents' | 'lab_results' | 'prescriptions' | 'imaging' | 'personal_info' | 'all';

interface PermissionGridSelectorProps {
  selectedCombinations: Array<{ permission: PermissionType; dataType: DataType }>;
  onSelectionChange: (combinations: Array<{ permission: PermissionType; dataType: DataType }>) => void;
  className?: string;
}

const PermissionGridSelector: React.FC<PermissionGridSelectorProps> = ({
  selectedCombinations,
  onSelectionChange,
  className
}) => {
  const permissionTypes = [
    {
      type: 'read' as PermissionType,
      label: 'Visualizar',
      icon: <Eye className="h-4 w-4" />,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-100'
    },
    {
      type: 'write' as PermissionType,
      label: 'Editar',
      icon: <Edit className="h-4 w-4" />,
      color: 'bg-green-500',
      lightColor: 'bg-green-100'
    },
    {
      type: 'download' as PermissionType,
      label: 'Baixar',
      icon: <Download className="h-4 w-4" />,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-100'
    },
    {
      type: 'share' as PermissionType,
      label: 'Compartilhar',
      icon: <Share className="h-4 w-4" />,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-100'
    },
    {
      type: 'delete' as PermissionType,
      label: 'Excluir',
      icon: <Trash2 className="h-4 w-4" />,
      color: 'bg-red-500',
      lightColor: 'bg-red-100'
    }
  ];

  const dataTypes = [
    {
      type: 'medical_records' as DataType,
      label: 'Registros Médicos',
      shortLabel: 'Registros',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-blue-700'
    },
    {
      type: 'documents' as DataType,
      label: 'Documentos',
      shortLabel: 'Docs',
      icon: <FileText className="h-4 w-4" />,
      color: 'text-green-700'
    },
    {
      type: 'lab_results' as DataType,
      label: 'Resultados de Exames',
      shortLabel: 'Exames',
      icon: <UserCheck className="h-4 w-4" />,
      color: 'text-purple-700'
    },
    {
      type: 'prescriptions' as DataType,
      label: 'Prescrições',
      shortLabel: 'Receitas',
      icon: <Pill className="h-4 w-4" />,
      color: 'text-orange-700'
    },
    {
      type: 'imaging' as DataType,
      label: 'Exames de Imagem',
      shortLabel: 'Imagens',
      icon: <Image className="h-4 w-4" />,
      color: 'text-pink-700'
    },
    {
      type: 'personal_info' as DataType,
      label: 'Informações Pessoais',
      shortLabel: 'Pessoais',
      icon: <UserCheck className="h-4 w-4" />,
      color: 'text-yellow-700'
    }
  ];

  const isSelected = (permission: PermissionType, dataType: DataType) => {
    return selectedCombinations.some(
      combo => combo.permission === permission && combo.dataType === dataType
    );
  };

  const toggleSelection = (permission: PermissionType, dataType: DataType) => {
    const isCurrentlySelected = isSelected(permission, dataType);
    
    if (isCurrentlySelected) {
      // Remove a combinação
      const newCombinations = selectedCombinations.filter(
        combo => !(combo.permission === permission && combo.dataType === dataType)
      );
      onSelectionChange(newCombinations);
    } else {
      // Adiciona a combinação
      const newCombinations = [...selectedCombinations, { permission, dataType }];
      onSelectionChange(newCombinations);
    }
  };

  const getPermissionData = (type: PermissionType) => {
    return permissionTypes.find(p => p.type === type);
  };

  const getDataTypeData = (type: DataType) => {
    return dataTypes.find(d => d.type === type);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Matriz de Permissões
          </CardTitle>
          <p className="text-sm text-gray-600">
            Selecione as combinações de permissões e tipos de dados que você precisa
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Cabeçalho com tipos de dados */}
              <div className="grid grid-cols-7 gap-1 mb-3">
                <div className="text-xs font-medium text-gray-500 p-1.5">
                  Permissões / Dados
                </div>
                {dataTypes.map((dataType) => (
                  <div key={dataType.type} className="text-center p-1">
                    <div className={`flex flex-col items-center space-y-0.5 ${dataType.color}`}>
                      {dataType.icon}
                      <span className="text-xs font-medium leading-tight">
                        {dataType.shortLabel}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Grid de permissões */}
              <div className="space-y-2">
                {permissionTypes.map((permission) => (
                  <div key={permission.type} className="grid grid-cols-7 gap-1">
                    {/* Cabeçalho da linha - tipo de permissão */}
                    <div className="flex items-center space-x-1.5 p-1.5">
                      <div className={`p-1 rounded-lg text-white ${permission.color}`}>
                        {permission.icon}
                      </div>
                      <span className="text-xs font-medium text-gray-700 leading-tight">
                        {permission.label}
                      </span>
                    </div>

                    {/* Células do grid */}
                    {dataTypes.map((dataType) => {
                      const selected = isSelected(permission.type, dataType.type);
                      const permissionData = getPermissionData(permission.type);
                      
                      return (
                        <div
                          key={`${permission.type}-${dataType.type}`}
                          className={`
                            relative cursor-pointer rounded-md border-2 transition-all duration-200 p-1.5
                            hover:shadow-sm hover:scale-102
                            ${
                              selected
                                ? `${permissionData?.lightColor} border-blue-500 ring-1 ring-blue-500`
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }
                          `}
                          onClick={() => toggleSelection(permission.type, dataType.type)}
                          title={`${permission.label} - ${dataType.label}`}
                        >
                          <div className="flex items-center justify-center h-6">
                            {selected ? (
                              <div className="flex flex-col items-center">
                                <Check className="h-3 w-3 text-green-600" />
                                <span className="text-xs font-medium text-gray-700 leading-none">
                                  Ativo
                                </span>
                              </div>
                            ) : (
                              <div className="w-3 h-3 border-2 border-gray-300 rounded" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo das seleções */}
      {selectedCombinations.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-3">
              Permissões Selecionadas ({selectedCombinations.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {selectedCombinations.map((combo, index) => {
                const permissionData = getPermissionData(combo.permission);
                const dataTypeData = getDataTypeData(combo.dataType);
                
                return (
                  <div
                    key={index}
                    className="flex items-center space-x-2 bg-white rounded-lg p-2 border"
                  >
                    <div className={`p-1 rounded text-white ${permissionData?.color}`}>
                      {permissionData?.icon}
                    </div>
                    <div className={`p-1 rounded ${dataTypeData?.color}`}>
                      {dataTypeData?.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {permissionData?.label}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {dataTypeData?.shortLabel}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PermissionGridSelector;